import type {
  TemperaturePoint,
  WeatherPoint,
} from '@ebalance/proto/bffuser/v1/weather_forecast_model_pb'

import { KOMA_COUNT } from '~/constants/koma'
import { isDaytimeHour, jstHour } from '~/models/weather/time'
import { vpfdEmojiOf } from '~/models/weather/vpfd'

/**
 * 3 時間ごとの天気予報 (VPFD 天気文) を 30 分刻み 48 スロットの絵文字列へ写す。
 * 予報は正時の点の値だけを扱うので (AC-WD-003a)、点のコマにだけ絵文字を置き、
 * 点と点の間・先頭点より前・末尾点より後のコマは空文字にする (据え置きも前方の
 * 引き継ぎもしない)。昼夜は点の時刻で決める。予報点が 1 つも無ければ空配列
 * (= 帯は空表示)。
 *
 * リクエストが from=to の単日指定である前提 (応答に他日の点は混ざらない)。
 */
export function buildKomaWeatherIcons(points: WeatherPoint[]): string[] {
  const icons: string[] = new Array<string>(KOMA_COUNT).fill('')
  let hasPoint = false
  for (const p of points) {
    if (p.targetDatetime === undefined) {
      continue
    }
    const hour = jstHour(p.targetDatetime)
    icons[hour * 2] = vpfdEmojiOf(p.weatherText, isDaytimeHour(hour))
    hasPoint = true
  }
  return hasPoint ? icons : []
}

/**
 * 1 時間ごとの気温予報を 48 スロットの数値系列 (℃、発表値のまま) へ写す。
 * 用途は表示ラベルでなく気象チャートの予想気温折れ線 (doji-bg 気温/予想気温)
 * なので、[[buildKomaTemperatureLabels]] と違い整数丸めしない。
 *
 * 予報は正時の点の値だけを扱うので (AC-WD-003a)、点のコマにだけ値を置き、
 * 点と点の間・先頭点より前・末尾点より後のコマは `null` (補間も据え置きもしない)。
 * 折れ線は呼び出し側の `connectNulls` で点どうしを結んで描き、ツールチップは
 * `null` のコマに予想気温を出さない。有効な点が 1 つも無ければ全 `null`。
 *
 * リクエストが from=to の単日指定である前提 (応答に他日の点は混ざらない)。
 */
export function buildKomaTemperatureForecastValues(
  points: TemperaturePoint[],
): Array<number | null> {
  const values = new Array<number | null>(KOMA_COUNT).fill(null)
  for (const p of points) {
    if (p.targetDatetime === undefined || p.temperature === undefined) {
      continue
    }
    const value = Number(p.temperature.value)
    if (Number.isNaN(value)) {
      continue
    }
    values[jstHour(p.targetDatetime) * 2] = value
  }
  return values
}

/**
 * [[buildKomaTemperatureForecastValues]] の数値系列を、天気帯に添える表示ラベル
 * (整数 ℃、単位なし) へ整える。値を持つコマ (= 正時の点) だけがラベルを持ち、他は
 * 空文字。有効な点が 1 つも無ければ空配列。
 *
 * リクエストが from=to の単日指定である前提 (応答に他日の点は混ざらない)。
 */
export function buildKomaTemperatureLabels(
  points: TemperaturePoint[],
): string[] {
  const values = buildKomaTemperatureForecastValues(points)
  if (values.every((v) => v === null)) {
    return []
  }
  return values.map((v) => (v === null ? '' : String(Math.round(v))))
}

/**
 * 天気帯にアイコンを置くコマ間隔 (6 コマ = 3 時間)。X 軸の時刻ラベル
 * (`chartFormatting.ts` の `formatChartTick` は 3 時間ごとの正時だけ描く) と
 * VPFD 予報の粒度に一致させる。6 の倍数は必ず偶数コマ = 正時なので、
 * 正時にだけ出る気温ラベルとも同じ列に載る。
 */
const STRIP_ICON_STEP_KOMA = 6

/**
 * 天気帯描画用の sparse 配列を組み立てる。3 時間ごとの正時コマ
 * (index 0 / 6 / 12 / ... / 42 = 00:00 / 03:00 / ... / 21:00 の 8 点) にのみ
 * 絵文字を残し、他のコマは空文字にする (= 48 コマ列のデータと描画間引きの分離)。
 * DetailChart / RegionChart の天気帯はこの結果をそのまま 1 コマ 1 セルで描画する。
 *
 * [設計変更 #5087] 旧実装は「同一絵文字が連続する run の中央コマにのみ置く」
 * (weather-icon-unify-48koma 設計書 grill Q8 / AC-07・09) だった。run 長が
 * データ次第で可変なため絵文字が 1 日 2〜5 個の不規則な間隔で並び、X 軸の
 * 3 時間ラベルとも対応しない見え方になっていたため、固定ピッチ配置へ変更した。
 * 同一天気が続いても間引かないので、絵文字は常に 8 個 (元コマが空文字なら空)。
 */
export function buildWeatherStripDisplayIcons(icons: string[]): string[] {
  return icons.map((icon, i) => (i % STRIP_ICON_STEP_KOMA === 0 ? icon : ''))
}

/**
 * 天気帯の左右ガター幅 (CSS `calc()` 文字列) を返す。素の軸幅から
 * **半コマぶん削る**ことで、48 個の等幅セルの中央が X 軸のコマ位置に一致する。
 *
 * 天気帯は 1 コマ 1 セルの等幅 flex なので、セル i の中央は
 * `プロット左端 + (i + 0.5) × プロット幅 / 48` (= band スケールの位置)。
 * 一方チャートの X 軸は `scale="point"` なので、コマ i は
 * `プロット左端 + i × プロット幅 / 47` に立つ。両者は最大で半コマ
 * (プロット幅の約 1%) ずれ、00:00 側と 21:00 側で符号が反転するため、
 * アイコンが時刻ラベル・グリッド線に対して内側へ寄って見える (#5087)。
 *
 * ガターを半コマ (= プロット幅 / 94) 削るとセル 1 個の幅が
 * `プロット幅 / 47` になり、セル中央がちょうどコマ位置に載る
 * (セル 0 の中央 = プロット左端、セル 47 の中央 = プロット右端)。
 * design プロトタイプ (`design/js/charts.js` の `layoutWeatherStrip`) が
 * `プロット左端 + (2h / 47) × プロット幅` へ実座標配置しているのと同じ整列で、
 * あちらの JS 実測に対しこちらは CSS だけで解く (プロット幅は実行時にしか
 * 決まらないので `100%` = 帯の親要素幅 から軸幅を引いて表す。親要素の幅は
 * チャートと同じ)。
 *
 * 端の 2 セルは中身が半コマぶん軸領域へはみ出す (絵文字・気温ラベルは
 * セル中央揃えのため)。行ラベルを持つ詳細画面のガターも同じだけ縮むので、
 * ラベルと 00:00 セルの間隔は変わらない (ただしガター自体はプロット幅に
 * 比例して細くなるので、ラベルが入り切らなくなる幅がある。逃がし方は
 * `DetailChart` の `WeatherStripRow` の JSDoc を参照)。
 *
 * **左右で表現方法が違う**。左は素の軸幅 (52〜84px) が半コマより十分大きいので
 * `width` から引けるが、右は右軸が出ていないと 2px しかなく `width` が負に
 * なる。CSS は負の `width` を 0 へクランプするため、そのままだと右だけ
 * 削れず、削れなかったぶん (半コマ − 右ガター幅) がほぼそのまま右端のずれとして
 * 残る = プロット幅に比例して増える (プロット幅 1,814px で約 17px、3,754px で
 * 約 37.5px。`koma.test.ts` の「右を width で削る旧形」ケースで固定している)。
 * つまり右が削れないと整列はほとんど回復しない。負値が有効な `margin` なら
 * 食い込めるので、右はスペーサーの `marginLeft` (`rightPull`) で半コマ取り崩す。
 * flex の空き幅計算は `width + margin` で行われるので、`width` を削るのと等価。
 */
export function weatherStripGutterWidths(
  leftGutterPx: number,
  rightGutterPx: number,
): { left: string; right: string; rightPull: string } {
  const halfKoma = `(100% - ${leftGutterPx}px - ${rightGutterPx}px) / ${2 * (KOMA_COUNT - 1)}`
  return {
    left: `calc(${leftGutterPx}px - ${halfKoma})`,
    right: `${rightGutterPx}px`,
    rightPull: `calc(-1 * (${halfKoma}))`,
  }
}
